/**
 * 🏛  Streets to Stability — Four Pillars · Shared Source of Truth
 * ══════════════════════════════════════════════════════════════════════
 * 🩹 Iter348 (2026-03-02) — DACE Sprint 1a
 *
 * Hoisted verbatim from `/app/frontend/src/pages/FourPillars.jsx` so the
 * public marketing site AND the internal Streets-to-Stability (STS DACE)
 * engine pull from ONE canonical constant. A copy edit here ripples to:
 *   · `/four-pillars`               — public marketing landing
 *   · `/admin/residents/:id`        — STS Touchpoint modal
 *   · Executive Dashboard chips     — Caseload Matrix + pace pins
 *   · SA LLM Node Review Board      — pillar filter chips
 *
 * IDs match the `sts_pillars` collection so backend + frontend never drift.
 *
 * ⚠️  DO NOT mutate this array at runtime. If SA wants to add a fifth
 * pillar in the future, extend the array + reseed `sts_pillars` in the
 * same PR.
 */
import {
  Heart, GraduationCap, BookOpen, Home as HomeIcon,
} from "lucide-react";

// Marketing photography — hand-picked Unsplash URLs approved by SA.
// Do NOT swap for AI-generated stock. See `FourPillars.jsx` for the
// original brief.
export const PILLARS = [
  {
    id:        "PILLAR_1_INNER",
    n:         1,
    title:     "Inner Strength & Case Management",
    subtitle:  "\"You cannot pour from an empty cup. We fill it first.\"",
    kicker:    "Pillar One · Foundation of Self",
    icon:      Heart,
    icon_key:  "Heart",
    accent:    "#B45309",         // amber-700 warm executive
    themes:    ["emotional_maturity", "identity", "guided_growth"],
    // 🖼 SA directive 2026-06 · portrait retired; landscape stock placeholder
    // (no person) — a rooted oak in open field, on-theme for "Foundation of Self".
    photo:     "https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&q=80&w=1600",
    photoAlt:  "A single mature oak standing rooted in an open field at golden hour — Inner Strength & Case Management",
    bullets: [
      "Case managers assigning weekly STS Road-Map homework nodes — trauma-informed prompts anchoring personal goals",
      "Emotional-identity rebuilt through structured homework submissions, milestone reviews, and accountability partners",
      "Coordinated support on housing stability, sobriety goals, and family reunification progress",
    ],
  },
  {
    id:        "PILLAR_2_VOCATION",
    n:         2,
    title:     "Vocation & Practical Life Skills",
    subtitle:  "\"Dignity is earned in the doing. Skill is the shield.\"",
    kicker:    "Pillar Two · The Workforce Bridge",
    icon:      GraduationCap,
    icon_key:  "GraduationCap",
    accent:    "#1B4332",         // deep evergreen — workforce
    themes:    ["employer_partnership", "financial_literacy", "practical_skills"],
    photo:     "https://customer-assets-jt897jd0.emergentagent.net/job_exchange-landing/artifacts/bt92we35_Heather%20Profile%20Pic.jpg",
    photoAlt:  "Warm portrait of Heather — Vocation & Practical Life Skills lead",
    // 🖼 PATCH-268 · Real staff portrait — same contain-fit treatment as
    // Pillars 01 and 04 so the head + shoulders sit comfortably inside
    // the pillar frame instead of being cropped.
    photoFit:  "contain",
    bullets: [
      "Employer-partner apprenticeship placements across Hospitality, Logistics, Skilled Trades, and Healthcare",
      "Financial-literacy labs — budgeting, credit repair, tax filing, savings ladders, and payoff planning",
      "Practical life-skill curriculum: cooking, transportation, healthcare navigation, and community civics",
    ],
  },
  {
    id:        "PILLAR_3_SPIRIT",
    n:         3,
    title:     "Christ-Centered Spiritual Counseling",
    subtitle:  "\"The anchor is not what floats you — it is what holds you.\"",
    kicker:    "Pillar Three · Anchoring the Heart",
    icon:      BookOpen,
    icon_key:  "BookOpen",
    accent:    "#7C5A00",         // deep gold — spiritual
    themes:    ["voluntary_pastoral", "scripture_identity", "worship_community"],
    photo:     "https://customer-assets-jt897jd0.emergentagent.net/job_exchange-landing/artifacts/ecslgs34_20260813_100547000_iOS.png",
    photoAlt:  "Warm portrait of the Christ-Centered Spiritual Counseling lead pastor",
    // 🖼 PATCH-268 · Real staff portrait — same contain-fit treatment as
    // Pillars 01 and 04 so the head + shoulders sit comfortably inside
    // the pillar frame instead of being cropped.
    photoFit:  "contain",
    bullets: [
      "Voluntary weekly pastoral counseling with credentialed chaplains — never coerced, always available",
      "Small-group scripture study rooted in identity, forgiveness, purpose, and Christ-centered restoration",
      "Prayer walks, worship nights, and communion meals that knit residents into the wider church family",
    ],
  },
  {
    id:        "PILLAR_4_DAILY",
    n:         4,
    title:     "The Daily Touchpoint & Residential Check-In",
    subtitle:  "\"Accountability is love, made visible every single day.\"",
    kicker:    "Pillar Four · Daily Accountability",
    icon:      HomeIcon,
    icon_key:  "Home",
    accent:    "#0A0612",         // ink-black — home base
    themes:    ["daily_walkthrough", "sanctuary_inspection", "milestone_visibility"],
    photo:     "https://customer-assets-jt897jd0.emergentagent.net/job_exchange-landing/artifacts/g7ak9981_Profile%20For%20Bob%20K.webp",
    photoAlt:  "Warm portrait of Bob K. — Daily Touchpoint & Residential Check-In lead",
    // 🖼 PATCH-266b · Real portrait, not a landscape stock photo — same
    // contain-fit treatment as Pillar 01 so the head + shoulders sit
    // comfortably inside the frame.
    photoFit:  "contain",
    bullets: [
      "Unit Ambassador daily walk-through — program-week-specific STS Road-Map homework questions and same-day escalation",
      "Sanctuary inspection rhythms tied to Non Compliance Notice / merit ledgers and paycheck-linked restitution accountability",
      "24-month Road-Map milestones surfacing every week: savings targets, employment velocity, and family goals",
    ],
  },
];

/** Convenience: pillar-by-id lookup. */
export const PILLAR_BY_ID = Object.fromEntries(PILLARS.map((p) => [p.id, p]));

/** Convenience: 1-based ordinal lookup (n=1..4). */
export const PILLAR_BY_N = Object.fromEntries(PILLARS.map((p) => [p.n, p]));

/** The four pillar ids as a stable tuple. Use for building queues, tabs,
 *  and iteration order in dashboards. */
export const PILLAR_IDS = PILLARS.map((p) => p.id);
